import { Component, OnInit } from '@angular/core';

import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { DynamicHeightDirective } from '../../../shared/directives/dynamic-height.directive';
import { AmountFormatDirective } from '../../../shared/directives/amount-format.directive';
import { SelectComponent, SelectOption } from '../../../shared/components/select/select.component';
import { DatePickerComponent } from '../../../shared/components/date-picker/date-picker.component';
import { ValidationService } from '../../../core/services/validation.service';
import { SnackbarService } from '../../../core/services/snackbar.service';
import { CaseEnquiryService } from '../../../core/services/case-enquiry.service';


@Component({
    selector: 'app-add-case-enquiry',
    standalone: true,
    imports: [ReactiveFormsModule, RouterModule, SelectComponent, DatePickerComponent, DynamicHeightDirective, AmountFormatDirective],
    templateUrl: './add-case-enquiry.component.html',
    styleUrl: './add-case-enquiry.component.scss'
})
export class AddCaseEnquiryComponent implements OnInit {
    enquiryForm: FormGroup;
    today = new Date();

    static amountValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (value === null || value === undefined || value === '') return null;
        const regex = /^\d+(\.\d{1,2})?$/;
        if (!regex.test(String(value))) {
            return { invalidAmount: true };
        }
        return null;
    }

    injuryTypeOptions: SelectOption[] = [
        { label: 'Motor Vehicle Accident', value: 'Motor Vehicle Accident' },
        { label: 'Slip and Fall', value: 'Slip and Fall' },
        { label: 'Medical Malpractice', value: 'Medical Malpractice' },
        { label: 'Workplace Injury', value: 'Workplace Injury' },
        { label: 'Other', value: 'Other' }
    ];

    settlementStatusOptions: SelectOption[] = [
        { label: 'In Pre-Suit Negotiations', value: 'PRE_SUIT' },
        { label: 'In Litigation', value: 'LITIGATION' },
        { label: 'Settled', value: 'SETTLED' }
    ];

    constructor(
        private fb: FormBuilder,
        private validationService: ValidationService,
        private router: Router,
        private snackbar: SnackbarService,
        private caseEnquiryService: CaseEnquiryService
    ) {
        this.enquiryForm = this.fb.group({
            client_first_name: ['', Validators.required],
            client_last_name: ['', Validators.required],
            injury_type: ['', Validators.required],
            date_of_incident: [null, Validators.required],
            lien_amount: [null, [Validators.required, Validators.min(0), AddCaseEnquiryComponent.amountValidator]],
            lien_details: [''], // Optional
            settlement_status: [null, Validators.required],
            settlement_amount: [null],
            settlement_loans: [null, Validators.required],
            loan_amount: [null],
            total_medicals: [null, [Validators.required, Validators.min(0), AddCaseEnquiryComponent.amountValidator]],
            legal_fees: [null, [Validators.required, Validators.min(0), AddCaseEnquiryComponent.amountValidator]],
            costs: [null, [Validators.required, Validators.min(0), AddCaseEnquiryComponent.amountValidator]]
        });
    }

    ngOnInit(): void {
        // Watch settlement_status for conditional validation on settlement_amount
        this.enquiryForm.get('settlement_status')?.valueChanges.subscribe(status => {
            const amountControl = this.enquiryForm.get('settlement_amount');
            if (status === 'SETTLED') {
                amountControl?.setValidators([Validators.required, Validators.min(0), AddCaseEnquiryComponent.amountValidator]);
            } else {
                amountControl?.clearValidators();
                amountControl?.setValue(null);
            }
            amountControl?.updateValueAndValidity();
        });

        // Watch settlement_loans for conditional validation on loan_amount
        this.enquiryForm.get('settlement_loans')?.valueChanges.subscribe(hasLoan => {
            const loanAmountControl = this.enquiryForm.get('loan_amount');
            if (hasLoan) {
                loanAmountControl?.setValidators([Validators.required, Validators.min(0), AddCaseEnquiryComponent.amountValidator]);
            } else {
                loanAmountControl?.clearValidators();
                loanAmountControl?.setValue(null);
            }
            loanAmountControl?.updateValueAndValidity();
        });
    }

    getErrorMessage(controlName: string, label: string): string | null {
        const control = this.enquiryForm.get(controlName);
        if (control && control.touched && control.errors) {
            return this.validationService.getValidatorErrorMessage(control.errors, label);
        }
        return null;
    }

    handleCancel() {
        this.router.navigate(['/case-enquiry']);
    }

    handleSave() {
        if (this.enquiryForm.valid) {
            const formValue = this.enquiryForm.value;
            
            // Format date to YYYY-MM-DD
            const dateOfIncident = new Date(formValue.date_of_incident);
            const formattedDate = dateOfIncident.toISOString().split('T')[0];

            // Construct payload matching the API spec
            const payload = {
                ...formValue,
                attorney_id: 1, // Hardcoded as per sample data request
                date_of_incident: formattedDate,
                inquiries_status: 'New'
            };

            this.caseEnquiryService.createEnquiry(payload).subscribe({
                next: () => {
                    this.snackbar.show('Case inquiry created successfully', 'success');
                    this.router.navigate(['/case-enquiry']);
                },
                error: () => {
                    this.snackbar.show('Failed to create case inquiry. Please try again.', 'error');
                }
            });
        } else {
            this.markFormGroupTouched(this.enquiryForm);
            this.snackbar.show('Please fix the errors in the form', 'error');
        }
    }

    private markFormGroupTouched(formGroup: FormGroup) {
        Object.values(formGroup.controls).forEach(control => {
            control.markAsTouched();
            if ((control as any).controls) {
                this.markFormGroupTouched(control as FormGroup);
            }
        });
    }
}
